import type { Styles } from '@adornis/ass/style.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { XFlex } from '@adornis/chemistry/elements/components/x-flex.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { XCheckbox } from '@adornis/forms/x-checkbox.js';
import '@adornis/forms/x-textarea.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import type { Instruction } from '@ils/osces/db/instruction.js';
import type { InstructionCategory } from '@ils/osces/db/instructionCategory.js';
import type { OSCE } from '@ils/osces/db/osce.js';
import { getSlotForRating } from '@ils/ratings/api/evaluation.js';
import {
  subscribeAllNonFinalRatingsByRatingID,
  updateInstructionNote,
  updateInstructionScoreOfRatingDoc,
} from '@ils/ratings/api/ratings.js';
import { calculateScoreOfRatingDoc } from '@ils/ratings/db/calculateScoreOfRatingDoc.js';
import { ExamSlotFinalRatingDoc } from '@ils/ratings/db/finalRating.js';
import { ExamSlotRatingDoc } from '@ils/ratings/db/rating.js';
import { ratingConfigs, type RatingConfig } from '@ils/ratings/db/ratingConfig.js';
import type { ExamSlotFinalRating } from '@ils/ratings/db/slotFinalRating.js';
import type { ExamSlotRating } from '@ils/ratings/db/slotRating.js';
import { RATING_CONFIG_NAMES, Specialization } from '@ils/static/enums.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { combineLatest, distinctUntilKeyChanged, filter, map, of, switchMap } from 'rxjs';
import type { ILSUser } from '../../../db/ils-user.js';
import { slotSelector } from './evaluation-slot-selector.js';

@customElement('ils-evaluation-rating')
export class ILSEvaluationRating extends ChemistryLitElement {
  @property({ attribute: false }) rating = new RXController<Maybe<ExamSlotRatingDoc>>(this, undefined);

  @state() private readonly _slotForRating = new RXController(
    this,
    this.rating.observable.pipe(filter(Boolean), distinctUntilKeyChanged('_id')),
    obs => obs.pipe(switchMap(rating => getSlotForRating(rating._id)(slotSelector))),
    { pipeIsLoading: true },
  );

  @state() private readonly _otherRatings = new RXController(
    this,
    this.rating.observable.pipe(filter(Boolean), distinctUntilKeyChanged('_id')),
    obs =>
      obs.pipe(
        switchMap(rating => {
          if (!(rating instanceof ExamSlotFinalRatingDoc)) return of([]);
          return subscribeAllNonFinalRatingsByRatingID(rating._id)(ExamSlotRatingDoc.allFields);
        }),
      ),
    { pipeIsLoading: true },
  );

  @state() private readonly _currentUser = new RXController(this, currentUser<ILSUser>());
  @state() private readonly _ownRating = new RXController(
    this,
    combineLatest([
      this._currentUser.observable.pipe(filter(Boolean)),
      this._otherRatings.observable.pipe(filter(Boolean)),
    ]).pipe(
      map(([user, ratings]) => {
        const ownRating = ratings.find(r => r.inspector === user._id);
        return ownRating;
      }),
    ),
  );
  @state() private readonly _otherRating = new RXController(
    this,
    combineLatest([
      this._currentUser.observable.pipe(filter(Boolean)),
      this._otherRatings.observable.pipe(filter(Boolean)),
    ]).pipe(
      map(([user, ratings]) => {
        const otherRating = ratings.find(r => r.inspector !== user._id);
        return otherRating;
      }),
    ),
  );

  override render() {
    const rating = this.rating.value;
    const slot = this._slotForRating.value;
    const osce = slot?.osce;

    const ownRating = this._ownRating.value;
    const otherRating = this._otherRating.value;

    if (
      this._slotForRating.isLoading ||
      this._otherRatings.isLoading ||
      !rating ||
      !slot ||
      !osce ||
      (rating instanceof ExamSlotFinalRatingDoc && (!ownRating || !otherRating))
    )
      return html`
        <x-flex space="lg" w100 h100 center crossaxis-center>
          <ils-loader> </ils-loader> <x-text> Bitte warten... </x-text>
        </x-flex>
      `;

    const ratingConfigName = osce.ratingConfigName ?? RATING_CONFIG_NAMES[0];
    const ratingConfig = ratingConfigs[ratingConfigName];
    if (!ratingConfig)
      return html`
        <x-text>
          Es gibt ein Problem mit der OSCE Konfiguration von OSCE '${osce._id}': Keine RatingConfig mit Namen
          '${ratingConfigName}' gefunden.
        </x-text>
      `;

    return html`
      ${this._scoreHeader({ osce, ratingConfig, ratingConfigName, rating, ownRating })}
      <x-grid columns="min-content 5fr 1fr 1fr 1fr 1fr" space="sm" ?disabled=${rating.committed}>
        ${repeat(osce.instructionCategories ?? [], (category, categoryIndex) => {
          const instructions = category.instructions;

          if (instructions.length === 0) return nothing;

          return html`
            ${this._categoryHeaders(category, ratingConfig)}
            ${repeat(instructions, (instruction, instructionIndex) => {
              if (!instruction) return nothing;
              const instructionRating = rating.ratings.find(r => r.instructionId === instruction._id);

              if (!instructionRating) return html` <x-text> Kein Rating für diese Instruction gefunden </x-text> `;

              if (rating instanceof ExamSlotFinalRatingDoc) {
                return this._finalEvaluation({
                  rating,
                  ownRating,
                  otherRating,
                  instructionRating,
                  instruction,
                  categoryIndex,
                  instructionIndex,
                  ratingConfig,
                });
              }
              return this._defaultEvaluation({
                rating,
                instructionRating,
                instruction,
                categoryIndex,
                instructionIndex,
                ratingConfig,
              });
            })}
          `;
        })}
      </x-grid>
    `;
  }

  private _scoreHeader({
    osce,
    ratingConfig,
    ratingConfigName,
    rating,
    ownRating,
  }: {
    osce: OSCE;
    ratingConfig: RatingConfig;
    ratingConfigName: string;
    rating: ExamSlotRatingDoc;
    ownRating?: ExamSlotRatingDoc;
  }) {
    const score = (calculateScoreOfRatingDoc(osce, ratingConfig, ratingConfigName, rating) * 100).toFixed(0);
    let text = `Aktuelle Bewertung: ${score}%`;
    if (rating instanceof ExamSlotFinalRatingDoc) {
      const ownScore = ownRating
        ? (calculateScoreOfRatingDoc(osce, ratingConfig, ratingConfigName, ownRating) * 100).toFixed(0)
        : undefined;
      text = `${text} (Vorherige Bewertung: ${ownScore}%)`;
    }
    return html`
      <x-flex
        ${css({
          position: 'sticky',
          top: this.spacing.xs,
          zIndex: '1',
          border: `2px solid ${this.colors.neutral.shade(100)}`,
          borderRadius: this.sizes.borderRadius,
          background: this.colors.white,
        })}
        padding="sm"
      >
        <x-text> ${text} </x-text>
        <x-tooltip>
          <x-text>
            ${rating instanceof ExamSlotFinalRatingDoc
              ? 'Nur Bewertungsitems mit Einigung werden in den Wert mit eingerechnet. (Alle Items mit einer umrandeten Checkbox werden nicht berücksichtigt)'
              : 'Nur Bewertungsitems mit einer Wertung werden in den Wert mit eingerechnet. (Alle Items ohne Haken werden nicht berücksichtigt)'}
          </x-text>
        </x-tooltip>
      </x-flex>
    `;
  }

  private _categoryHeaders(category: InstructionCategory, ratingConfig: RatingConfig) {
    return html`
      <x-flex padding="sm" ${css({ gridColumn: '1 / 3', paddingLeft: '0', paddingTop: this.spacing.lg })}>
        <x-h3> ${category.description} </x-h3>
      </x-flex>
      ${ratingConfig.header(this)}
    `;
  }

  private _defaultEvaluation({
    rating,
    instructionRating,
    instruction,
    categoryIndex,
    instructionIndex,
    ratingConfig,
  }: {
    rating: ExamSlotRatingDoc;
    instructionRating: ExamSlotRating;
    instruction: Instruction;
    categoryIndex: number;
    instructionIndex: number;
    ratingConfig: RatingConfig;
  }) {
    return html`
      <x-flex padding="sm" space="md" horizontal end> ${this._instructionNr(categoryIndex, instructionIndex)} </x-flex>
      <!-- description -->
      <x-flex space="xs">
        ${this._instructionDescription(instruction)}
        <!-- note -->
        ${this._noteInput({ ratingID: rating._id, instructionRating, categoryIndex, instructionIndex })}
      </x-flex>
      ${repeat([...Array(ratingConfig.choices)], (_, score) =>
        this._scoreCheckbox({
          ratingID: rating._id,
          instructionRating,
          score,
          ownScore: instructionRating.score,
          span: 4 / ratingConfig.choices,
          disabled: rating.committed,
        }),
      )}
    `;
  }

  private _finalEvaluation({
    rating,
    ownRating,
    otherRating,
    instructionRating,
    instruction,
    categoryIndex,
    instructionIndex,
    ratingConfig,
  }: {
    rating: ExamSlotFinalRatingDoc;
    ownRating?: ExamSlotRatingDoc;
    otherRating?: ExamSlotRatingDoc;
    instructionRating: ExamSlotFinalRating;
    instruction: Instruction;
    categoryIndex: number;
    instructionIndex: number;
    ratingConfig: RatingConfig;
  }) {
    const ownInstructionRating = ownRating?.ratings.find(r => r.instructionId === instruction._id);
    const otherInstructionRating = otherRating?.ratings.find(r => r.instructionId === instruction._id);

    return html`
      <x-flex padding="sm" space="md" horizontal end>
        <x-icon ${css({ opacity: instructionRating.score === -1 ? 1 : 0, color: this.colors.tone.warning })}>
          warning
        </x-icon>
        ${this._instructionNr(categoryIndex, instructionIndex)}
      </x-flex>
      <x-flex space="xs">
        <!-- description -->
        ${this._instructionDescription(instruction)}
        <!-- note -->
        ${this._noteInput({
          ratingID: ownRating?._id,
          instructionRating: ownInstructionRating,
          categoryIndex,
          instructionIndex,
        })}
      </x-flex>
      ${repeat([...Array(ratingConfig.choices)], (_, score) => {
        let mark = false;

        const ownSpecialization = this._currentUser.value?.specialization;
        const otherSpecialization =
          ownSpecialization === Specialization.DOCTOR ? Specialization.LINGUIST : Specialization.DOCTOR;
        if (!ownSpecialization) return;
        const voteKeyMap = { [Specialization.DOCTOR]: 'voteDoctor', [Specialization.LINGUIST]: 'voteLinguist' };

        if (instructionRating.score === -1) {
          if (instructionRating[voteKeyMap[otherSpecialization]] === score) mark = true;
        }

        const isAgreed =
          !ownInstructionRating ||
          !otherInstructionRating ||
          ownInstructionRating.score === otherInstructionRating.score;

        const agreedScore = isAgreed ? ownInstructionRating?.score : undefined;

        const disabled =
          rating.committed ||
          (ownSpecialization === Specialization.DOCTOR ? rating.doctorCommitted : rating.linguistCommitted) ||
          isAgreed;

        return this._scoreCheckbox({
          ratingID: rating._id,
          instructionRating,
          score,
          ownScore: instructionRating[voteKeyMap[ownSpecialization]],
          mark,
          span: 4 / ratingConfig.choices,
          disabled,
          isAgreed,
          agreedScore,
        });
      })}
    `;
  }

  private _scoreCheckbox({
    ratingID,
    instructionRating,
    score,
    ownScore,
    mark = false,
    span,
    disabled,
    isAgreed,
    agreedScore,
  }: {
    ratingID: string;
    instructionRating: ExamSlotRating;
    score: number;
    ownScore: number;
    mark?: boolean;
    span: number;
    disabled: boolean;
    isAgreed?: boolean;
    agreedScore?: number;
  }) {
    return html`
      <x-flex
        crossaxis-center
        ?checkbox=${!disabled}
        ${css({ gridColumn: `span ${span}`, paddingTop: this.spacing.sm })}
        @click=${(e: MouseEvent) => {
          if (disabled) return;
          const xflex = e.currentTarget as XFlex;
          const checkbox = xflex.children[0];
          if (checkbox instanceof XCheckbox && !e.composedPath().includes(checkbox)) {
            // @ts-expect-error
            checkbox._toggle();
          }
        }}
      >
        <x-checkbox
          ?disabled=${disabled}
          ${css({
            border: `2px solid ${mark ? this.colors.tone.warning : 'transparent'}`,
            borderRadius: this.sizes.borderRadius,
          })}
          .value=${isAgreed ? score === agreedScore : score === ownScore}
          @value-picked=${(e: ValueEvent<boolean>) =>
            updateInstructionScoreOfRatingDoc(ratingID, instructionRating.instructionId, e.detail.value ? score : -1)()}
        ></x-checkbox>
      </x-flex>
    `;
  }

  private _instructionNr(categoryIndex: number, instructionIndex: number) {
    return html` <x-text> ${categoryIndex + 1}.${instructionIndex + 1} </x-text> `;
  }

  private _instructionDescription(instruction: Instruction) {
    return html`
      <x-flex horizontal crossaxis-center space="sm" ${css({ paddingTop: '5px' })}>
        <x-text> ${unsafeHTML(instruction.question)} </x-text>
        ${instruction.description
          ? html`
              <x-button
                mode="icon"
                icon="help_outline"
                @click=${() => XDialog.alert(html` <x-text> ${unsafeHTML(instruction.description)} </x-text> `)}
              ></x-button>
            `
          : nothing}
      </x-flex>
    `;
  }

  private _noteInput({ ratingID, instructionRating }: { ratingID?: string; instructionRating: ExamSlotRating }) {
    if (!ratingID) return nothing;

    return html`
      <x-text>
        <x-plain-textarea
          placeholder="Notiz"
          tone="subtle"
          .value=${instructionRating.notes}
          @value-picked=${async (e: ValueEvent<string>) => {
            const note = e.detail.value;
            if (note !== instructionRating.notes) {
              await updateInstructionNote(ratingID, instructionRating.instructionId, note)();
            }
          }}
          ${css({ paddingBottom: this.spacing.md, color: this.colors.neutral.shade(500) })}
        ></x-plain-textarea>
      </x-text>
    `;
  }

  override styles(): Styles[] {
    return [
      ...super.styles(),
      {
        '[checkbox]': { border: '1px solid transparent', borderRadius: this.sizes.borderRadius, cursor: 'pointer' },
        '[checkbox]:hover': { border: `1px solid ${this.colors.neutral.shade(50)}` },
      },
    ];
  }
}

const preWrapper = (text: string) => {
  return html`
    <pre
      ${css({
        margin: '0',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        maxWidth: '50vw',
        fontFamily: 'Lato',
      })}
    >
${text}</pre
    >
  `;
};
